import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { collection, addDoc, serverTimestamp, doc, runTransaction } from "firebase/firestore";
import Decimal from "decimal.js";
import { format } from 'date-fns-tz';

import { db } from "./firebaseConfig";
import logUserAction from './utils/logUserAction.js';

import {
  hotDrinks, coldDrinks, cookies, cookieBox, Glasses, Sunglasses, Readers, CustomItem,
  Americano, Beurreberry, Cappuccino, Caramel, ChaiLatte, Chipster, ChocolatChaud,
  Choconoix, ColdCoffee, //CoffeeBeans, //ColdDrink, 
  Espresso, Latte, Lemube,
  Macciato, Mocha, Matcha, MatchaLatte, MatchaCold, MatchaEspresso, Noisette, Nougato, Nuage,
  Oat, Pistou, Scarlette, Sucranelle, The, Toffeo, Vanilla, DarkSugar, SimpleSyrop, CookieMonster, Easter, Oreo, Milk, Water
} from './images';

const LoadingSpinner = () => (
  <div className="fixed inset-0 flex justify-center items-center bg-white z-50">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-500"></div>
  </div>
);

const categories = [
  { name: "Hot Drinks", image: hotDrinks },
  { name: "Cold Drinks", image: coldDrinks },
  { name: "Cookies", image: cookies },
  { name: "Cookie Box", image: cookieBox },
  { name: "Glasses", image: Glasses },
  { name: "Custom Item", image: CustomItem },
];

const extras = [
  { name: "Oat Milk", price: 1.0, image: Oat },
  { name: "Espresso Shot", price: 1.0, image: Espresso },
  { name: "Vanilla", price: 0, image: Vanilla },
  { name: "Salted Caramel", price: 0, image: Caramel },
  { name: "Brown Sugar", price: 0, image: DarkSugar },
  { name: "Simple Syrop", price: 0, image: SimpleSyrop },
];

const products = {
  Cookies: [
    { name: "Chipster", price: 4.35, image: Chipster },
    { name: "Noisette", price: 4.35, image: Noisette },
    { name: "Choconoix", price: 4.35, image: Choconoix },
    { name: "Beurreberry", price: 4.35, image: Beurreberry },
    { name: "Pistou", price: 4.35, image: Pistou },
    { name: "Nuage", price: 4.35, image: Nuage },
    { name: "Sucranelle", price: 4.35, image: Sucranelle },
    { name: "Lemube", price: 4.35, image: Lemube },
    { name: "Scarlette", price: 4.35, image: Scarlette },
    { name: "Tofféo", price: 4.35, image: Toffeo },
    { name: "Nougato", price: 4.35, image: Nougato },
    { name: "Matcha", price: 4.35, image: Matcha },
    { name: "Oreo", price: 4.35, image: Oreo },
    { name: "Cookie Monster", price: 4.35, image: CookieMonster },
    { name: "PomPom", price: 4.35, image: Sucranelle },
    { name: "Funfetti", price: 4.35, image: Chipster }
  ],
  "Hot Drinks": [
    { name: "Espresso", price: 3.25, image: Espresso },
    { name: "Macciato", price: 3.5, image: Macciato },
    { name: "Americano", price: 3.5, image: Americano },
    { name: "Mocha", price: 5.30, image: Mocha },
    { name: "Matcha Latté", price: 5.30, image: MatchaLatte },
    { name: "Matcha Esspresso", price: 5.50, image: MatchaEspresso },
    { name: "Latté", price: 4.35, image: Latte },
    { name: "Cappuccino", price: 4.35, image: Cappuccino },
    { name: "Chocolat Chaud", price: 4.30, image: ChocolatChaud },
    { name: "Thé / Tisane", price: 2.90, image: The },
    { name: "Chai Latté", price: 5.30, image: ChaiLatte },
    // { name: "Grains de Café", price: 8.99, image: CoffeeBeans },
  ],
  "Cold Drinks": [
    { name: "Iced Latte", price: 4.35, image: ColdCoffee },
    { name: "Iced Matcha", price: 5.35, image: MatchaCold },
    { name: "Water", price: 2.00, image: Water },
    { name: "Milk", price: 2.00, image: Milk },
    // { name: "Iced Tea", price: 3.25, image: ColdDrink },
  ],
  "Glasses": [
    { name: "Readers", price: 39.99, image: Readers, taxable: true },
    { name: "Sunglasses", price: 39.99, image: Sunglasses, taxable: true },
  ]
};

const GST_RATE = 0.05;
const QST_RATE = 0.09975;

const toCurrency = (num) => new Decimal(num).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();

const OrderPage = ({ user, userStore, isOnline }) => {

  const navigate = useNavigate();

  const paymentModalRef = useRef(null);
  const orderSummaryRef = useRef(null);
  const orderCompletedRef = useRef(null);

  const isAdmin = user?.admin === true;
  const isOwner = user?.role === "Owner";
  const isStaff = user?.role === "Staff";
  const isAuthorized = (isAdmin || isOwner || isStaff) && user?.approved === true;

  const [selectedStore, setSelectedStore] = useState("");
  const [order, setOrder] = useState([]);
  const [delayDone, setDelayDone] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showOrderSummaryModal, setShowOrderSummaryModal] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [customizingProduct, setCustomizingProduct] = useState(null);
  const [cookieBoxSelection, setCookieBoxSelection] = useState([]);
  const [cookieCounts, setCookieCounts] = useState({});

  const [customItemName, setCustomItemName] = useState("");
  const [customItemPrice, setCustomItemPrice] = useState("");
  const [customItemTax, setCustomItemTax] = useState(false);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentMethodsUsed, setPaymentMethodsUsed] = useState([]);
  const [splitMode, setSplitMode] = useState(false);
  const [manualAmount, setManualAmount] = useState('');
  const [remainingAmount, setRemainingAmount] = useState(0);

  const [orderCompletedModal, setOrderCompletedModal] = useState(false);
  const [orderBeingSent, setOrderBeingSent] = useState(false);
  const [orderIdCreated, setOrderIdCreated] = useState(null);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [changeDue, setChangeDue] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [receiptSnapshot, setReceiptSnapshot] = useState(null);
  const [emailSentTo, setEmailSentTo] = useState("");
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const MONTREAL_TIMEZONE = 'America/Toronto';
  const localTimestamp = format(new Date(), 'yyyy-MM-dd HH:mm:ss zzz', { timeZone: MONTREAL_TIMEZONE });

  const discountOptions = useMemo(() => [
    { title: "None", percent: 0 },
    { title: "15% UnionMD Building", percent: 15 },
    { title: "50% Employee Discount", percent: 50 }
  ], []);

  const [discount, setDiscount] = useState({ title: "None", percent: 0 });

  const clientTime = new Date();

  const showCookieBoxFooter = selectedCategory === "Cookie Box";
  const showCustomizationFooter = customizingProduct !== null;

  const groupOrderItems = (order) => {
    const grouped = [];

    order.forEach((item) => {
      const existingIndex = grouped.findIndex(
        (groupedItem) =>
          groupedItem.name === item.name &&
          JSON.stringify(groupedItem.extras || []) === JSON.stringify(item.extras || [])
      );

      const extrasTotal = (item.extras || []).reduce((sum, extra) => {
        return sum + (extra.price * extra.quantity);
      }, 0);

      const itemPriceWithExtras = item.price + extrasTotal;

      if (existingIndex > -1) {
        // Increase quantity of existing item
        grouped[existingIndex].quantity += item.quantity;
      } else {
        grouped.push({
          ...item,
          price: itemPriceWithExtras,
          quantity: item.quantity,
        });
      }
    });

    return grouped;
  };

  const groupedItems = groupOrderItems(order).map(item => ({
    ...item,
    total: toCurrency(item.price * item.quantity)
  }));


  const subtotal = groupedItems.reduce((sum, item) => {
    return sum + item.total;
  }, 0);

  const discountValue = toCurrency((subtotal * discount.percent) / 100);

  const isItemTaxable = (item) => {
    const NON_TAXABLE = ["Cookie Box", "Boîte de biscuits"];
    return item.taxable === true || (!NON_TAXABLE.includes(item.name) && item.taxable !== false);
  };


  const taxableSubtotal = groupedItems
    .filter(item => isItemTaxable(item))
    .reduce((sum, item) => sum + item.total, 0);

  let gst = toCurrency(taxableSubtotal * GST_RATE);
  let qst = toCurrency(taxableSubtotal * QST_RATE);

  if (discount.title === "50% Employee Discount") {
    gst = toCurrency(gst * 0.5);
    qst = toCurrency(qst * 0.5);
  }

  const finalSubtotal = toCurrency(subtotal - discountValue);
  const grandTotal = toCurrency(
    Number(finalSubtotal.toFixed(2)) +
    Number(gst.toFixed(2)) +
    Number(qst.toFixed(2))
  );
  const roundedTotal = toCurrency(Math.round(grandTotal * 20) / 20);
  const totalTaxes = toCurrency(gst + qst);

  const resetOrder = useCallback(() => {
    setOrder([]);
    setPaymentMethod(null);
    setDiscount(discountOptions[0]);
    setRemainingAmount(grandTotal);
    setPaymentMethodsUsed([]);
    setManualAmount('');
    setCookieCounts({});
    setSelectedCategory(null);
  }, [grandTotal, discountOptions]);

  const handleAddToOrder = (product, isCookie = false) => {

    setOrder((prevOrder) => {

      const existingItemIndex = prevOrder.findIndex(
        (item) => item.name === product.name && !item.extras
      );

      if (existingItemIndex !== -1) {
        const updatedOrder = [...prevOrder];
        const existingItem = updatedOrder[existingItemIndex];
        updatedOrder[existingItemIndex] = {
          ...existingItem,
          quantity: existingItem.quantity + 1,
        };
        return updatedOrder;
      }

      const newOrder = [
        ...prevOrder,
        { ...product, quantity: 1, extras: product.extras || [] },
      ];

      return newOrder;
    });

    if (isCookie) {
      setCookieCounts((prevCounts) => ({
        ...prevCounts,
        [product.name]: (prevCounts[product.name] || 0) + 1,
      }));
    }

    setSelectedExtras([]);
  };

  const finalizeTransaction = async (paymentsArray) => {
    if (isProcessing) return; // Prevent multiple clicks
    setIsProcessing(true);

    if (!isOnline) {
      setErrorMessage("You are offline. Orders cannot be submitted.");
      return;
    }

    try {
      const newOrderID = await getNextOrderID();

      const orderData = {
        orderID: newOrderID,
        items: groupedItems.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: toCurrency(item.price),
          total: toCurrency(item.price * item.quantity),
          extras: item.extras || []
        })),
        discount: discount.title,
        discountPercent: discount.percent,
        subtotal: toCurrency(subtotal - discountValue).toFixed(2),
        discountValue: toCurrency(discountValue).toFixed(2),
        grandTotal: toCurrency(paymentMethod === "Cash" ? roundedTotal : grandTotal).toFixed(2),
        gst: toCurrency(gst).toFixed(2),
        qst: toCurrency(qst).toFixed(2),
        paymentMethods: paymentsArray,
        userId: user?.uid || "Unknown",
        userName: user?.displayName || "No Name",
        userEmail: user?.email || "No Email",
        customerEmail: customerEmail || "",
        timestamp: new Date()
      };

      const storeToSave = isAdmin ? selectedStore : (user?.store || userStore);

      await addDoc(collection(db, "orders"), {
        ...orderData,
        clientCreatedAt: clientTime,
        store: storeToSave,
        createdAt: serverTimestamp(),
      });
      setReceiptSnapshot({
        orderID: newOrderID,
        items: groupedItems.map(item => ({
          name: item.name,
          quantity: item.quantity,
          total: item.total.toFixed(2)
        })),
        subtotal: subtotal.toFixed(2),
        discountValue: discountValue.toFixed(2),
        gst: gst.toFixed(2),
        qst: qst.toFixed(2),
        grandTotal: grandTotal.toFixed(2),
        timestamp: localTimestamp
      });

      if (customerEmail) {
        console.log("📧 Sending receipt to:", customerEmail);
        console.log("Payload:", orderData);

        fetch("https://tifsu-ordering.onrender.com/api/send-receipt", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(orderData),
        })
          .then(async (res) => {
            const text = await res.text();
            console.log("📨 Raw response text:", text);

            try {
              const json = JSON.parse(text);
              console.log("✅ JSON parsed:", json);
            } catch (parseErr) {
              console.error("❌ JSON parse error:", parseErr);
            }
          })
          .catch(err => {
            console.error("Failed to send receipt email:", err);
            alert("❌ Failed to send email.");
          });
      }

      await logUserAction(user, "Created Order", {
        orderID: newOrderID,
        store: isAdmin ? selectedStore : userStore,
        subtotal: finalSubtotal.toFixed(2)
      });

      const audio = new Audio("/notification.mp3");
      audio.play();

      setShowPaymentModal(false);
      setOrderCompletedModal(true);
      setOrderIdCreated(newOrderID);

      if (paymentsArray.some(p => p.method === "Cash")) {
        const totalCashPaid = paymentsArray
          .filter(p => p.method === "Cash")
          .reduce((sum, p) => sum + p.amount, 0);

        const change = toCurrency(totalCashPaid - roundedTotal);
        setChangeDue(change > 0 ? change : 0);
      } else {
        setChangeDue(0);
      }

      const storeInventoryRef = doc(db, "storeInventory", storeToSave);

      await runTransaction(db, async (transaction) => {
        const storeDoc = await transaction.get(storeInventoryRef);
        if (!storeDoc.exists()) throw new Error("Store inventory not found");

        const data = storeDoc.data();
        const updatedCookies = { ...data.cookies };
        let hotCupsUsed = 0;
        let coldCupsUsed = 0;
        let boxesUsed = 0;

        groupedItems.forEach((item) => {
          const name = item.name;

          // Deduct cookie box contents
          if (name.startsWith("(Box) ")) {
            const cookieName = name.replace("(Box) ", "");
            if (updatedCookies[cookieName] !== undefined) {
              updatedCookies[cookieName] = Math.max(0, updatedCookies[cookieName] - item.quantity);
            }
          }

          // Deduct normal cookies
          if (updatedCookies[name] !== undefined) {
            updatedCookies[name] = Math.max(0, updatedCookies[name] - item.quantity);
          }

          // Deduct hot cups (except espresso/macchiato)
          const isHotDrink = products["Hot Drinks"].some(p => p.name === name);
          if (isHotDrink && !["Espresso", "Macciato"].includes(name)) {
            hotCupsUsed += item.quantity;
          }

          // Deduct cold cups
          const isColdDrink = products["Cold Drinks"].some(p => p.name === name);
          if (isColdDrink) {
            coldCupsUsed += item.quantity;
          }

          // Deduct boxes
          if (name === "Cookie Box") {
            boxesUsed += item.quantity;
          }
        });

        transaction.update(storeInventoryRef, {
          cookies: updatedCookies,
          hotCups: Math.max(0, data.hotCups - hotCupsUsed),
          coldCups: Math.max(0, data.coldCups - coldCupsUsed),
          cookieBoxes: Math.max(0, data.cookieBoxes - boxesUsed),
        });
      });

      // } catch (error) {
      //   console.error("🔥 FinalizeTransaction error:", error?.message || error);
      //   alert("Error saving order. Try again.");
      // 
    } finally {
      setIsProcessing(false);
      setOrderBeingSent(false);
    }

  };

  const handleAddPayment = () => {
    const amount = parseFloat(manualAmount);
    if (!paymentMethod) {
      setErrorMessage("Select a payment method.");
      return;
    }

    if (isNaN(amount) || amount <= 0) {
      setErrorMessage("Enter a valid amount.");
      return;
    }

    // Handle Cash overpayment (change)
    if (paymentMethod === "Cash") {
      const updatedPayments = [...paymentMethodsUsed, { method: "Cash", amount: toCurrency(amount) }];
      const newRemaining = toCurrency(roundedTotal - amount);

      setPaymentMethodsUsed(updatedPayments);
      setRemainingAmount(newRemaining < 0 ? 0 : newRemaining);
      setManualAmount("");

      if (newRemaining <= 0) {
        finalizeTransaction(updatedPayments);
      }

      return;
    }

    // Prevent overpayment for card-based methods
    if (amount > remainingAmount) {
      setErrorMessage(`Amount exceeds remaining balance of $${remainingAmount.toFixed(2)}`);
      return;
    }

    // Normal case
    const updatedPayments = [...paymentMethodsUsed, { method: paymentMethod, amount: toCurrency(amount) }];
    const newRemaining = remainingAmount - amount;

    setPaymentMethodsUsed(updatedPayments);
    setRemainingAmount(newRemaining);
    setManualAmount("");

    if (newRemaining <= 0) {
      finalizeTransaction(updatedPayments);
    }
  };

  const handleExactAmount = () => {
    if (!paymentMethod) {
      setErrorMessage("Select a payment method.");
      return;
    }

    const amount = remainingAmount;
    const updatedPayments = [...paymentMethodsUsed, { method: paymentMethod, amount: toCurrency(amount) }];
    setPaymentMethodsUsed(updatedPayments);
    setRemainingAmount(0);
    setManualAmount('');

    finalizeTransaction(updatedPayments);
  };

  const printInvoice = () => {
    const printWindow = window.open("", "", "width=860,height=900");

    const logoURL = "/LOGO tifsu biscuits DARK.png"; // public folder

    // Build box → flavor grouping for invoice
    const cookieBoxes = [];
    let boxIndex = -1;

    groupedItems.forEach(item => {
      if (item.name === "Boîte de biscuits" || item.name === "Cookie Box") {
        cookieBoxes.push({ main: item, flavors: [] });
        boxIndex++;
      }
      else if (item.name.startsWith("(Box) ") && boxIndex >= 0) {
        cookieBoxes[boxIndex].flavors.push(item);
      }
    });

    const mainItems = groupedItems.filter(i =>
      !i.name.startsWith("(Box) ") &&
      i.name !== "Boîte de biscuits" &&
      i.name !== "Cookie Box"
    );


    const html = `
  <html>
  <head>
    <title>TIFSU Order</title>
    <style>
      body {
        font-family: Inter, -apple-system, BlinkMacSystemFont, Arial;
        padding: 40px;
        color: #222;
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .logo {
        max-height: 80px;
      }

      .store-info {
        text-align: right;
        font-size: 13px;
      }

      h1 {
        margin-top: 30px;
        text-align: center;
        font-size: 26px;
        letter-spacing: 1px;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 24px;
      }

      th {
        text-align: left;
        font-size: 13px;
        border-bottom: 2px solid #444;
        padding-bottom: 8px;
      }

      td {
        padding: 8px 0;
        font-size: 14px;
      }

      .right {
        text-align: right;
      }

      .sub {
        padding-left: 20px;
        font-size: 13px;
        color: #555;
      }

      .section-line {
        border-bottom: 1px dashed #aaa;
        margin: 20px 0;
      }

      .totals {
        max-width: 350px;
        margin-left: auto;
        margin-top: 20px;
        font-size: 14px;
      }

      .totals div {
        display: flex;
        justify-content: space-between;
        margin-top: 6px;
      }

      .grand {
        font-size: 18px;
        font-weight: bold;
        border-top: 2px solid #333;
        padding-top: 6px;
        margin-top: 8px;
      }

      .footer {
        margin-top: 40px;
        font-size: 12px;
        text-align: center;
        color: #666;
      }

    </style>
  </head>
  <body>

  <div class="header">
    <img class="logo" src="${logoURL}" />
    <div class="store-info">
      <div><strong>Boutique :</strong> Marché Jean-Talon</div>
      <div><strong>Date :</strong> ${localTimestamp}</div>
    </div>
  </div>

  <h1>RÉSUMÉ DE COMMANDE</h1>

  <table>
    <thead>
      <tr>
        <th>Article</th>
        <th>Qté</th>
        <th class="right">Prix unitaire</th>
        <th class="right">Total</th>
      </tr>
    </thead>
    <tbody>

    ${cookieBoxes.map(box => `
  <tr>
    <td>${box.main.name}</td>
    <td>${box.main.quantity}</td>
    <td class="right">$${toCurrency(box.main.price).toFixed(2)}</td>
    <td class="right">$${box.main.total.toFixed(2)}</td>
  </tr>

  ${box.flavors.map(f => `
    <tr>
      <td class="sub">↳ ${f.quantity}× ${f.name.replace("(Box) ", "")}</td>
      <td></td>
      <td class="right">$0.00</td>
      <td class="right">$0.00</td>
    </tr>
  `).join("")}
`).join("")}

${mainItems
        .filter(i => i.name !== "Boîte de biscuits")
        .map(item => `
    <tr>
      <td>${item.name}</td>
      <td>${item.quantity}</td>
      <td class="right">$${toCurrency(item.price).toFixed(2)}</td>
      <td class="right">$${item.total.toFixed(2)}</td>
    </tr>
  `).join("")}

    </tbody>
  </table>

  <div class="section-line"></div>

  <div class="totals">
    <div><span>Sous-total</span><span>$${finalSubtotal.toFixed(2)}</span></div>
    <div><span>TPS</span><span>$${gst.toFixed(2)}</span></div>
    <div><span>TVQ</span><span>$${qst.toFixed(2)}</span></div>
    <div class="grand"><span>Total</span><span>$${grandTotal.toFixed(2)}</span></div>
  </div>

  <div class="footer">
    tifsu Biscuits · Paiement en attente <br />
  </div>

  <script>
    window.print();
  </script>

  </body>
  </html>
  `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const updateExtraQuantity = (extra, increment = 1, isToggle = false) => {
    setSelectedExtras((prevExtras) => {
      const existingExtra = prevExtras.find((e) => e.name === extra.name);

      // Handle toggle for "Oat Milk"
      if (isToggle) {
        if (existingExtra) {
          return prevExtras.filter((e) => e.name !== extra.name); // Deselect
        } else {
          return [...prevExtras, { ...extra, quantity: 1 }]; // Select
        }
      }

      if (existingExtra) {
        const updatedQuantity = existingExtra.quantity + increment;
        if (updatedQuantity <= 0) {
          return prevExtras.filter((e) => e.name !== extra.name);
        }
        return prevExtras.map((e) =>
          e.name === extra.name ? { ...e, quantity: updatedQuantity } : e
        );
      } else if (increment > 0) {
        return [...prevExtras, { ...extra, quantity: 1 }];
      }
      return prevExtras;
    });
  };

  const ExtrasGrid = ({ extras, selectedExtras, updateExtraQuantity }) => (
    <div className="grid grid-cols-3 gap-2 mb-32">
      {extras.map((extra) => {
        const selected = selectedExtras.find((e) => e.name === extra.name);
        const quantity = selected ? selected.quantity : 0;
        const isOat = extra.name === "Oat Milk";
        const selectedStyle = isOat && quantity > 0 ? "bg-green-100 border-green-400 shadow-lg" : "";

        return (
          <div
            key={extra.name}
            onClick={() => isOat && updateExtraQuantity(extra, 1, true)}
            className={`border p-4 rounded-lg bg-white hover:shadow-md flex flex-col items-center transition-shadow cursor-pointer ${selectedStyle}`}
          >
            <img src={extra.image} alt={extra.name} className="w-24 h-24 object-contain mb-4 rounded" />
            <h3 className="text-lg font-bold text-[#4c5d99] text-center mb-2">{extra.name}</h3>
            <p className="text-gray-600 mb-4">${extra.price.toFixed(2)}</p>

            {!isOat && (
              <div className="flex items-center justify-center w-full border rounded overflow-hidden">
                <button
                  onClick={(e) => { e.stopPropagation(); updateExtraQuantity(extra, -1); }}
                  className="flex-1 py-2 bg-red-500 text-white text-xl hover:bg-red-600">-</button>
                <div className="flex-1 py-2 text-center font-bold text-lg border-l border-r">{quantity}</div>
                <button
                  onClick={(e) => { e.stopPropagation(); updateExtraQuantity(extra, 1); }}
                  className="flex-1 py-2 bg-green-500 text-white text-xl hover:bg-green-600">+</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const handleAddCustomItem = () => {
    if (!customItemName.trim() || isNaN(customItemPrice) || parseFloat(customItemPrice) <= 0) {
      setErrorMessage("Please enter a valid item name and price.");
      return;
    }

    const newItem = {
      name: customItemName,
      price: parseFloat(customItemPrice),
      quantity: 1,
      taxable: customItemTax,
      extras: []
    };

    setOrder((prevOrder) => [...prevOrder, newItem]);
    setCustomItemName("");
    setCustomItemPrice("");
    setCustomItemTax(false);
    setErrorMessage(""); // ✅ Clear error after successful add
  };

  const finalizeCookieBox = () => {
    const totalQuantity = cookieBoxSelection.reduce((sum, item) => sum + item.quantity, 0);
    if (totalQuantity !== 6) {
      alert("You must select exactly 6 cookies to complete the box.");
      return;
    }

    const cookieBox = {
      name: "Boîte de biscuits",
      price: 26,
      quantity: 1,
      taxable: false
    };

    let boxIndex = -1;

    // Add $0 cookie breakdowns as individual line items
    const breakdownItems = cookieBoxSelection.map((cookie) => ({
      name: `(Box) ${cookie.name}`,
      price: 0,
      quantity: cookie.quantity,
      extras: []
    }));

    setOrder((prevOrder) => [...prevOrder, cookieBox, ...breakdownItems]);

    setCookieBoxSelection([]);
    setSelectedCategory(null);
  };

  const openPaymentModal = () => {
    setPaymentMethod(null);
    setShowPaymentModal(true);
    setSplitMode(false);
    setRemainingAmount(grandTotal); // or roundedTotal if cash
  };

  const alwaysShowBackToCategories = ["Hot Drinks", "Cold Drinks", "Cookies", "Glasses", "Custom Item"];

  const getNextOrderID = async () => {
    const counterRef = doc(db, "orderCounters", "globalCounter");

    const newOrderID = await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(counterRef);

      if (!docSnap.exists()) {
        throw new Error("Global counter does not exist");
      }

      const current = docSnap.data().orderID || 1200;
      const next = current + 1;

      transaction.update(counterRef, { orderID: next });

      return next;
    });

    return newOrderID;
  };

  const BottomActionBar = ({ children }) => (
    <div className="fixed bottom-0 left-0 w-full bg-white px-4 py-3 border-t flex justify-between items-center z-50 shadow-lg">
      {children}
    </div>
  );


  const FixedActionButton = ({ disabled, onClick, label, variant = 'green' }) => {
    const baseStyles = 'px-6 py-3 text-lg font-semibold rounded-lg transition';
    const disabledStyles = 'bg-gray-400 text-white cursor-not-allowed';
    const greenStyles = 'bg-green-500 hover:bg-green-600 text-white';
    const redStyles = 'bg-red-500 hover:bg-red-600 text-white';
    const grayStyles = 'bg-gray-300 hover:bg-gray-400 text-black';

    const getVariantStyle = () => {
      if (disabled) return disabledStyles;
      switch (variant) {
        case 'green':
          return greenStyles;
        case 'red':
          return redStyles;
        case 'gray':
        default:
          return grayStyles;
      }
    };

    return (
      <button
        disabled={disabled}
        onClick={onClick}
        className={`${baseStyles} ${getVariantStyle()}`}
      >
        {label}
      </button>
    );
  };



  useEffect(() => {
    const timer = setTimeout(() => setDelayDone(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (showPaymentModal) {
      const totalPaid = paymentMethodsUsed.reduce((sum, p) => sum + p.amount, 0);
      const newRemaining = (paymentMethod === "Cash" ? roundedTotal : grandTotal) - totalPaid;
      setRemainingAmount(toCurrency(Math.max(0, newRemaining)));
    }
  }, [discount, roundedTotal, grandTotal, paymentMethod, showPaymentModal, paymentMethodsUsed]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (orderSummaryRef.current && !orderSummaryRef.current.contains(e.target)) {
        setShowOrderSummaryModal(false);
      }
    };

    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setShowOrderSummaryModal(false);
      }
    };

    if (showOrderSummaryModal) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("keydown", handleEsc);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("keydown", handleEsc);
    };
  }, [showOrderSummaryModal]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (orderCompletedRef.current && !orderCompletedRef.current.contains(e.target) && !orderBeingSent) {
        resetOrder();
        setOrderCompletedModal(false);
        setOrderIdCreated(null);
        setChangeDue(0);
      }
    };

    const handleEsc = (e) => {
      if (e.key === "Escape" && !orderBeingSent) {
        resetOrder();
        setOrderCompletedModal(false);
        setOrderIdCreated(null);
        setChangeDue(0);
      }
    };

    if (orderCompletedModal) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("keydown", handleEsc);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("keydown", handleEsc);
    };
  }, [orderCompletedModal, orderBeingSent, resetOrder]);

  if (!isAuthorized && delayDone) {
    return (
      <div className="fixed inset-0 flex justify-center items-center bg-white z-50">
        <div className="bg-white border border-gray-300 shadow-lg rounded-lg p-8 max-w-sm w-full text-center">
          <div className="flex justify-center mb-4">
            <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mb-4" />
          </div>
          <h2 className="text-xl font-bold mb-2 text-gray-800">Access Denied</h2>
          <p className="text-gray-600 mb-6">You must be logged in to view orders.</p>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!delayDone || !user) {
    return <LoadingSpinner />;
  }

  const handleClearOrder = () => {
    const confirmClear = window.confirm("Are you sure you want to clear your order?");
    if (confirmClear) {
      setOrder([]);
      setCookieCounts({});
    }
  };

  const handleRemoveFromOrder = (product) => {
    setOrder((prevOrder) => {
      const updatedOrder = [...prevOrder];
      const indexToRemove = updatedOrder.findIndex(
        (item) =>
          item.name === product.name &&
          JSON.stringify(item.extras || []) === JSON.stringify(product.extras || [])
      );

      if (indexToRemove > -1) {
        if (updatedOrder[indexToRemove].quantity > 1) {
          updatedOrder[indexToRemove].quantity -= 1;
        } else {
          updatedOrder.splice(indexToRemove, 1);
        }
      }

      return updatedOrder;
    });

    setCookieCounts((prevCounts) => {
      if (prevCounts[product.name]) {
        const updatedCounts = { ...prevCounts };
        if (updatedCounts[product.name] > 1) {
          updatedCounts[product.name] -= 1;
        } else {
          delete updatedCounts[product.name];
        }
        return updatedCounts;
      }
      return prevCounts;
    });
  };

  const updateCookieBoxSelection = (cookie, increment) => {
    setCookieBoxSelection((prevSelection) => {
      const existing = prevSelection.find((item) => item.name === cookie.name);
      const currentTotal = prevSelection.reduce((sum, item) => sum + item.quantity, 0);

      if (increment > 0 && currentTotal >= 6) return prevSelection;

      if (existing) {
        const updatedQuantity = existing.quantity + increment;
        if (updatedQuantity <= 0) {
          return prevSelection.filter((item) => item.name !== cookie.name);
        }
        return prevSelection.map((item) =>
          item.name === cookie.name ? { ...item, quantity: updatedQuantity } : item
        );
      } else if (increment > 0) {
        return [...prevSelection, { ...cookie, quantity: 1 }];
      }
      return prevSelection;
    });
  };

  const totalPaid = paymentMethodsUsed.reduce((sum, p) => sum + p.amount, 0);
  const amountToPay = toCurrency(
    (paymentMethod === "Cash" ? roundedTotal : grandTotal) - totalPaid
  );





  return (
    <div className="container mx-auto p-4">
      {errorMessage && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded relative">
          {errorMessage}
          <button
            onClick={() => setErrorMessage('')}
            className="absolute top-0 right-0 mt-1 mr-2 text-red-500 hover:text-red-700"
          >
            &times;
          </button>
        </div>
      )}

      {customizingProduct ? (
        <>
          <h2 className="text-xl font-bold mb-4 text-[#4c5d99]">
            Customize: {customizingProduct.name}
          </h2>

          <ExtrasGrid
            extras={extras}
            selectedExtras={selectedExtras}
            updateExtraQuantity={updateExtraQuantity}
          />

          <BottomActionBar>
            {showCustomizationFooter ? (
              <>
                <FixedActionButton
                  label="Return to Products"
                  variant="gray"
                  onClick={() => {
                    setCustomizingProduct(null);
                    setSelectedExtras([]);
                  }}
                />
                <FixedActionButton
                  label="Add to Order"
                  variant="green"
                  onClick={() => {
                    handleAddToOrder({
                      ...customizingProduct,
                      extras: [...selectedExtras],
                    });
                    setCustomizingProduct(null);
                    setSelectedExtras([]);
                  }}
                />

              </>
            ) : showCookieBoxFooter ? (
              <>
                <FixedActionButton label="Back to Categories" variant="gray" onClick={() => setSelectedCategory(null)} />
                <FixedActionButton
                  label="Add Cookie Box to Order"
                  variant="green"
                  disabled={cookieBoxSelection.reduce((total, item) => total + item.quantity, 0) !== 6}
                  onClick={finalizeCookieBox}
                />
              </>
            ) : (
              <>
                {alwaysShowBackToCategories.includes(selectedCategory) ? (
                  <FixedActionButton label="Back to Categories" variant="gray" onClick={() => setSelectedCategory(null)} />
                ) : (
                  <FixedActionButton
                    label="Clear Order"
                    variant={order.length > 0 ? "red" : "gray"}
                    disabled={order.length === 0}
                    onClick={handleClearOrder}
                  />
                )}

                <FixedActionButton
                  label="View Summary"
                  variant="gray"
                  disabled={order.length === 0}
                  onClick={() => setShowOrderSummaryModal(true)}
                />

                <FixedActionButton
                  label={isProcessing ? "Processing..." : "Send Order"}
                  variant="green"
                  disabled={!isOnline || order.length === 0 || (isAdmin && !selectedStore) || isProcessing}
                  onClick={openPaymentModal}
                />
              </>
            )}
          </BottomActionBar>
        </>
      ) : (
        <>
          {/* ✅ Normal Page Layout */}
          {isAdmin && (
            <>
              <div className="mb-2">
                <h3 className="text-lg font-bold mb-2 text-[#4c5d99]">Select Store</h3>
              </div>
              <div className="mb-2 p-4 border rounded-lg bg-gray-100">
                <select
                  value={selectedStore}
                  onChange={(e) => setSelectedStore(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none"
                >
                  <option value="">Select a store</option>
                  <option value="Jean-Talon O">Jean-Talon O</option>
                  <option value="Jean-Talon Market">Jean-Talon Market</option>
                  <option value="Events">Events</option>
                  <option value="Test">Test</option>
                </select>
              </div>
            </>
          )}

          {!selectedCategory ? (
            <div className="grid grid-cols-2 gap-2">
              {categories.map((category) => (
                <div
                  key={category.name}
                  className="cursor-pointer text-center border p-4 rounded-lg bg-[#9faee5] hover:bg-[#7c8ac4] transition"
                  onClick={() => setSelectedCategory(category.name)}
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-32 object-contain rounded-lg"
                  />
                  <h2 className="mt-2 text-lg font-bold text-white">{category.name}</h2>
                </div>
              ))}
            </div>
          ) : (
            <>
              {selectedCategory === "Cookie Box" ? (
                <div>
                  <h2 className="text-xl font-bold mb-4">Build Your Cookie Box</h2>

                  <div className="grid grid-cols-3 gap-2">
                    {products["Cookies"].map((cookie) => {
                      const selectedCookie = cookieBoxSelection.find(
                        (item) => item.name === cookie.name
                      );
                      const quantity = selectedCookie ? selectedCookie.quantity : 0;

                      return (
                        <div
                          key={cookie.name}
                          className="border rounded-lg p-4 bg-white shadow hover:shadow-md flex flex-col items-center transition-shadow"
                        >
                          <img
                            src={cookie.image}
                            alt={cookie.name}
                            className="w-24 h-24 object-contain mb-4 rounded"
                          />
                          <h3 className="text-lg font-bold text-center mb-2">{cookie.name}</h3>
                          <p className="text-gray-600 mb-4">${cookie.price.toFixed(2)}</p>

                          <div className="flex items-center justify-center w-full border rounded overflow-hidden">
                            <button
                              onClick={() => updateCookieBoxSelection(cookie, -1)}
                              className="flex-1 py-3 bg-red-500 text-white text-2xl font-bold hover:bg-red-600"
                            >
                              -
                            </button>

                            <div className="flex-1 py-3 bg-white text-center font-semibold text-lg border-l border-r">
                              {quantity}
                            </div>

                            <button
                              onClick={() => updateCookieBoxSelection(cookie, 1)}
                              className="flex-1 py-3 bg-green-500 text-white text-2xl font-bold hover:bg-green-600"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : selectedCategory === "Custom Item" ? (
                <div className="bg-gray-100 p-4 rounded-lg">
                  <div className="flex justify-center items-center min-h-[60vh]">
                    <div className="flex flex-col gap-6 p-8 bg-white rounded-2xl shadow-xl max-w-2xl w-full">

                      <h2 className="text-2xl font-bold text-center text-[#4c5d99]">Add Custom Item</h2>

                      {/* Item Name Row */}
                      <div className="relative w-full border rounded-xl bg-white focus-within:ring-2 focus-within:ring-indigo-300">
                        <input
                          type="text"
                          placeholder="Item Name"
                          value={customItemName}
                          onChange={(e) => setCustomItemName(e.target.value)}
                          className="px-6 py-4 text-lg border rounded-xl focus:outline-none w-full"
                        />
                      </div>

                      {/* Price + Add Tax Row */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                        {/* Price Input */}
                        <div className="relative w-full border rounded-xl bg-white focus-within:ring-2 focus-within:ring-indigo-300">
                          {customItemPrice === "" && (
                            <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none select-none font-mono">
                              $0.00
                            </span>
                          )}
                          <input
                            type="tel"
                            inputMode="numeric"
                            value={
                              customItemPrice === ""
                                ? ""
                                : `$${Number(customItemPrice).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`
                            }
                            onChange={(e) => {
                              const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 9);
                              const formatted = (parseInt(digitsOnly || "0", 10) / 100).toFixed(2);
                              setCustomItemPrice(formatted);
                            }}
                            onFocus={() => {
                              if (customItemPrice === "") setCustomItemPrice("");
                            }}
                            className="pr-4 py-4 w-full text-lg font-mono text-right text-black focus:outline-none bg-transparent"
                          />
                        </div>


                        {/* Tax Toggle */}
                        <div
                          onClick={() => setCustomItemTax(!customItemTax)}
                          className={`flex justify-center items-center text-lg font-medium text-gray-800 px-5 py-4 rounded-xl cursor-pointer border transition ${customItemTax
                            ? "bg-green-200 border-gray-400"
                            : "bg-white border-gray-300 hover:bg-gray-100"
                            }`}
                        >
                          {customItemTax ? "Tax Applied ✅" : "Add Tax?"}
                        </div>
                      </div>

                      {/* Add Button */}
                      <button
                        onClick={handleAddCustomItem}
                        className="self-center px-8 py-4 text-lg font-semibold bg-green-500 text-white rounded-xl hover:bg-green-600"
                      >
                        Add Item to Order
                      </button>
                    </div>
                  </div>

                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-2 max-w-screen-xl mx-auto">
                  {products[selectedCategory].map((product) => (
                    <div
                      key={product.name}
                      className="relative border rounded-lg bg-white hover:bg-[#d3d9f7] cursor-pointer transition overflow-hidden"
                      onClick={() => {
                        if (selectedCategory === "Cookies") {
                          handleAddToOrder(product, true);
                        } else if (selectedCategory === "Glasses") {
                          handleAddToOrder({ ...product, taxable: true });
                        } else if (selectedCategory === "Cold Drinks" && (product.name === "Water" || product.name === "Milk")) {
                          handleAddToOrder(product);
                        } else {
                          setCustomizingProduct(product);
                        }
                      }}
                    >
                      {/* Product Image */}
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-32 object-contain p-2"
                      />

                      {/* Name + Price Bubble (bottom-left) */}
                      <div className="absolute bottom-2 left-2 bg-white bg-opacity-100 text-black text-xs px-2 py-1 rounded-md">
                        <span className="font-semibold">{product.name}</span> – ${product.price.toFixed(2)}
                      </div>

                      {/* Cookie Count Bubble (bottom-right) */}
                      {selectedCategory === "Cookies" && cookieCounts[product.name] > 0 && (
                        <div className="absolute bottom-2 right-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-lg">
                          {cookieCounts[product.name]}x
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}



          {/* Order Summary */}
          <div className="mt-8 border-t pt-4">

            {/* Action Buttons */}
            {customizingProduct ? (
              <div>

                {/* Customize Title */}
                <h2 className="text-xl font-bold mb-4 text-[#4c5d99]">
                  Customize: {customizingProduct.name}
                </h2>

                {/* Extras Grid */}
                <ExtrasGrid
                  extras={extras}
                  selectedExtras={selectedExtras}
                  updateExtraQuantity={updateExtraQuantity}
                />

                {/* Fixed Buttons */}
                <BottomActionBar>
                  {showCustomizationFooter ? (
                    <>
                      <FixedActionButton
                        label="Return to Products"
                        variant="gray"
                        onClick={() => {
                          setCustomizingProduct(null);
                          setSelectedExtras([]);
                        }}
                      />
                      <FixedActionButton
                        label="Add to Order"
                        variant="green"
                        onClick={() => {
                          handleAddToOrder({
                            ...customizingProduct,
                            extras: [...selectedExtras],
                          });
                          setCustomizingProduct(null);
                          setSelectedExtras([]);
                        }}
                      />

                    </>
                  ) : showCookieBoxFooter ? (
                    <>
                      <FixedActionButton label="Back to Categories" variant="gray" onClick={() => setSelectedCategory(null)} />
                      <FixedActionButton
                        label="Add Cookie Box to Order"
                        variant="green"
                        disabled={cookieBoxSelection.reduce((total, item) => total + item.quantity, 0) !== 6}
                        onClick={finalizeCookieBox}
                      />
                    </>
                  ) : (
                    <>
                      {alwaysShowBackToCategories.includes(selectedCategory) ? (
                        <FixedActionButton label="Back to Categories" variant="gray" onClick={() => setSelectedCategory(null)} />
                      ) : (
                        <FixedActionButton
                          label="Clear Order"
                          variant={order.length > 0 ? "red" : "gray"}
                          disabled={order.length === 0}
                          onClick={handleClearOrder}
                        />
                      )}

                      <FixedActionButton
                        label="View Summary"
                        variant="gray"
                        disabled={order.length === 0}
                        onClick={() => setShowOrderSummaryModal(true)}
                      />
                      <FixedActionButton
                        label="Send Order"
                        variant="green"
                        disabled={!isOnline || order.length === 0 || (isAdmin && !selectedStore)}
                        onClick={openPaymentModal}
                      />
                    </>
                  )}
                </BottomActionBar>
              </div>
            ) : (
              <>
                <BottomActionBar>
                  {showCustomizationFooter ? (
                    <>
                      <FixedActionButton
                        label="Return to Products"
                        variant="gray"
                        onClick={() => {
                          setCustomizingProduct(null);
                          setSelectedExtras([]);
                        }}
                      />
                      <FixedActionButton
                        label="Add to Order"
                        variant="green"
                        onClick={() => {
                          handleAddToOrder({
                            ...customizingProduct,
                            extras: [...selectedExtras],
                          });
                          setCustomizingProduct(null);
                          setSelectedExtras([]);
                        }}
                      />

                    </>
                  ) : showCookieBoxFooter ? (
                    <>
                      <FixedActionButton label="Back to Categories" variant="gray" onClick={() => setSelectedCategory(null)} />
                      <FixedActionButton
                        label="Add Cookie Box to Order"
                        variant="green"
                        disabled={cookieBoxSelection.reduce((total, item) => total + item.quantity, 0) !== 6}
                        onClick={finalizeCookieBox}
                      />
                    </>
                  ) : (
                    <>
                      {alwaysShowBackToCategories.includes(selectedCategory) ? (
                        <FixedActionButton label="Back to Categories" variant="gray" onClick={() => setSelectedCategory(null)} />
                      ) : (
                        <FixedActionButton
                          label="Clear Order"
                          variant={order.length > 0 ? "red" : "gray"}
                          disabled={order.length === 0}
                          onClick={handleClearOrder}
                        />
                      )}

                      <FixedActionButton
                        label="View Summary"
                        variant="gray"
                        disabled={order.length === 0}
                        onClick={() => setShowOrderSummaryModal(true)}
                      />
                      <FixedActionButton
                        label="Send Order"
                        variant="green"
                        disabled={!isOnline || order.length === 0 || (isAdmin && !selectedStore)}
                        onClick={openPaymentModal}
                      />
                    </>
                  )}
                </BottomActionBar>
              </>
            )}


            {showOrderSummaryModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div
                  ref={orderSummaryRef}
                  className="bg-white p-8 rounded-lg max-w-4xl w-full space-y-6 relative">
                  <h2 className="text-2xl font-bold text-center mb-4 text-[#4c5d99]">Order Summary</h2>

                  {/* Scrollable Order Items */}
                  <div className="max-h-[500px] overflow-y-auto space-y-4 px-2">
                    {groupedItems.length === 0 && (
                      <p className="text-center text-gray-500">No items in the order.</p>
                    )}

                    {groupedItems.map((item, index) => {
                      const NON_TAXABLE = ["Cookie Box", "Boîte de biscuits"];
                      const isTaxable = item.taxable === true || (!NON_TAXABLE.includes(item.name) && item.taxable !== false);
                      const base = item.total;
                      const gstAmount = isTaxable ? toCurrency(base * GST_RATE) : 0;
                      const qstAmount = isTaxable ? toCurrency(base * QST_RATE) : 0;
                      const totalItemPrice = toCurrency(base + gstAmount + qstAmount);

                      return (
                        <div
                          key={index}
                          className="border rounded p-4 shadow flex justify-between items-start gap-2"
                        >
                          {/* Item Info */}
                          <div className="flex-1">
                            <h3 className="font-bold text-[#4c5d99]">{item.name}</h3>
                            <p className="text-sm text-gray-600">Qty: {item.quantity}</p>

                            {Array.isArray(item.extras) && item.extras.length > 0 && (
                              <ul className="text-sm text-gray-500 mt-1">
                                {item.extras.map((extra, idx) => (
                                  <li key={idx}>
                                    + {extra.quantity}x {extra.name} (${(extra.quantity * extra.price).toFixed(2)})
                                  </li>
                                ))}
                              </ul>
                            )}

                            {Array.isArray(item.items) && item.items.length > 0 && (
                              <ul className="text-sm text-gray-500 mt-1">
                                {item.items.map((cookie, idx) => (
                                  <li key={idx}>
                                    - {cookie.quantity}x {cookie.name}
                                  </li>
                                ))}
                              </ul>
                            )}

                            <div className="flex gap-2 text-xs text-gray-500 mt-2">
                              <p>Base: ${item.total.toFixed(2)}</p>
                              {isTaxable && (
                                <>
                                  <p>GST: ${gstAmount.toFixed(2)}</p>
                                  <p>QST: ${qstAmount.toFixed(2)}</p>
                                </>
                              )}
                            </div>

                            <p className="font-bold mt-1 text-[#4c5d99] text-sm">
                              Total: ${totalItemPrice.toFixed(2)}
                            </p>
                          </div>

                          {/* Remove Button */}
                          <button
                            onClick={() => handleRemoveFromOrder(item)}
                            className="text-red-500 hover:text-red-700 text-sm px-2 py-1 border border-red-500 rounded"
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Totals Section */}
                  <div className="border-t pt-4 text-right text-sm space-y-1">
                    <p>Subtotal: ${subtotal.toFixed(2)}</p>
                    <p>Discount: -${discountValue.toFixed(2)} ({discount.percent}%)</p>
                    <p>GST: ${gst.toFixed(2)}</p>
                    <p>QST: ${qst.toFixed(2)}</p>
                    <p>Total Taxes: ${totalTaxes.toFixed(2)}</p>
                    <p className="text-lg font-bold text-[#4c5d99]">Grand Total: ${grandTotal.toFixed(2)}</p>
                  </div>

                  {/* Close Button */}
                  <div className="flex gap-3">
                    <button
                      onClick={printInvoice}
                      className="flex-1 py-3 rounded bg-gray-600 hover:bg-gray-700 text-white text-lg"
                    >
                      Print / Save PDF
                    </button>

                    <button
                      onClick={() => setShowOrderSummaryModal(false)}
                      className="flex-1 py-3 rounded bg-indigo-500 hover:bg-indigo-600 text-white text-lg"
                    >
                      Close
                    </button>
                  </div>

                </div>
              </div>
            )}

            {showPaymentModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div
                  ref={paymentModalRef}
                  className="bg-white p-8 rounded-lg max-w-md w-full space-y-6">
                  <h2 className="text-xl font-bold text-center mb-4">Complete Payment</h2>

                  {/* Payment Options */}
                  <div className="grid grid-cols-3 gap-2">
                    {["Visa", "Mastercard", "Debit", "Amex", "Cash"].map((method) => (
                      <button
                        key={method}
                        onClick={() => setPaymentMethod(method)}
                        className={`p-2 rounded border transition ${paymentMethod === method
                          ? "bg-indigo-500 text-white border-indigo-600"
                          : "bg-gray-100 border-gray-300 hover:bg-indigo-100"
                          }`}
                      >
                        <img
                          src={`/icons/${method}.png`}
                          alt={method}
                          className="h-8 mx-auto mb-2 object-contain"
                        />
                        <span className="text-sm">{method}</span>
                      </button>
                    ))}
                  </div>

                  {!paymentMethodsUsed.some(p => p.method === "Cash") && (
                    <div className="flex justify-center mt-4">
                      <button
                        onClick={() => setSplitMode(!splitMode)}
                        className="text-blue-600 underline text-sm"
                      >
                        {splitMode ? "Cancel Split Payment" : "Use Split Payment"}
                      </button>
                    </div>
                  )}


                  <div>
                    <label className="block font-semibold mb-1">Discount</label>
                    <select
                      value={discount.title}
                      onChange={(e) => {
                        const selected = discountOptions.find(
                          (opt) => opt.title === e.target.value
                        );
                        setDiscount(selected);
                      }}
                      className="w-full border px-3 py-2 rounded"
                    >
                      {discountOptions.map((opt) => (
                        <option key={opt.title} value={opt.title}>
                          {opt.title} ({opt.percent}%)
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Totals Display */}
                  <div className="border-t pt-4 space-y-2">
                    <p>Subtotal: ${(subtotal - discountValue).toFixed(2)}</p>
                    <p>Discount: -${discountValue.toFixed(2)}</p>
                    <p>GST: ${gst.toFixed(2)}</p>
                    <p>QST: ${qst.toFixed(2)}</p>
                    <p className="font-bold text-lg">
                      Grand Total: ${grandTotal.toFixed(2)}
                    </p>
                    {paymentMethod === "Cash" && (
                      <p className="font-bold text-md text-red-600">
                        Cash Rounded Total: ${roundedTotal.toFixed(2)}
                      </p>
                    )}
                    <p className="font-bold text-md text-blue-600">
                      Remaining Amount: ${remainingAmount.toFixed(2)}
                    </p>

                    {paymentMethod === "Cash" && (
                      (() => {
                        const totalCashPaid = paymentMethodsUsed
                          .filter(p => p.method === "Cash")
                          .reduce((sum, p) => sum + p.amount, 0);

                        const change = toCurrency(totalCashPaid - roundedTotal);
                        return change > 0 ? (
                          <p className="font-bold text-md text-green-600">
                            Change to Give: ${change.toFixed(2)}
                          </p>
                        ) : null;
                      })()
                    )}

                  </div>

                  {/* Split Payment Mode */}
                  {(splitMode || paymentMethod === "Cash") && (
                    <div className="flex flex-col space-y-2 mt-4">
                      <input
                        type="number"
                        min="0"
                        placeholder="Enter Amount"
                        value={manualAmount}
                        onChange={(e) => setManualAmount(e.target.value)}
                        disabled={!paymentMethod}
                        className="w-full border px-3 py-2 rounded"
                      />

                      <button
                        onClick={handleAddPayment}
                        disabled={!paymentMethod || !manualAmount}
                        className={`w-full py-2 rounded ${!paymentMethod || !manualAmount
                          ? "bg-gray-300 cursor-not-allowed"
                          : "bg-blue-500 hover:bg-blue-600 text-white"
                          }`}
                      >
                        Accept Payment of ${manualAmount || 0}{" "}
                        {paymentMethod && `(${paymentMethod})`}
                      </button>

                      {splitMode && (
                        <button
                          onClick={handleExactAmount}
                          disabled={!paymentMethod || remainingAmount <= 0}
                          className={`w-full py-2 rounded ${!paymentMethod || remainingAmount <= 0
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-green-500 hover:bg-green-600 text-white"
                            }`}
                        >
                          Pay Remaining (${remainingAmount.toFixed(2)}){" "}
                          {paymentMethod && `by ${paymentMethod}`}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Payments Already Added */}
                  <div className="mt-4">
                    <h3 className="font-semibold mb-2">Payments Made</h3>
                    {paymentMethodsUsed.length === 0 && (
                      <p className="text-gray-500 text-sm">No payments yet.</p>
                    )}
                    <ul className="space-y-1">
                      {paymentMethodsUsed.map((p, idx) => (
                        <li
                          key={idx}
                          className="flex justify-between text-sm text-gray-800"
                        >
                          <div className="flex items-center gap-2">
                            <img
                              src={`/icons/${p.method}.png`}
                              alt={p.method}
                              className="h-5 w-5 object-contain"
                            />
                            {p.method}
                          </div>
                          <span>${p.amount.toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Confirm Payment (Simple Mode) */}
                  {!splitMode && (
                    <button
                      onClick={() => {
                        if (!paymentMethod) {
                          alert("Select a payment method.");
                          return;
                        }

                        const totalPaid = paymentMethodsUsed.reduce((sum, p) => sum + p.amount, 0);
                        const amountToPay = toCurrency(
                          (paymentMethod === "Cash" ? roundedTotal : grandTotal) - totalPaid
                        );

                        const updatedPayments = [
                          ...paymentMethodsUsed,
                          { method: paymentMethod, amount: amountToPay },
                        ];

                        if (totalPaid > 0) {
                          // If there's already partial payment, force split-mode logic
                          finalizeTransaction(updatedPayments);
                        } else {
                          // Otherwise treat as single payment
                          finalizeTransaction(updatedPayments);
                        }
                      }}

                      disabled={!isOnline || !paymentMethod}
                      className={`w-full py-2 rounded ${!isOnline || !paymentMethod
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-green-500 hover:bg-green-600 text-white"
                        }`} >
                      {isOnline ? (
                        totalPaid > 0
                          ? `Pay Remaining ($${amountToPay.toFixed(2)}) by ${paymentMethod}`
                          : `Confirm Payment of $${(paymentMethod === "Cash" ? roundedTotal : grandTotal).toFixed(2)}`
                      ) : "Offline - Cannot Send Order"}

                    </button>

                  )}

                  <button
                    onClick={() => {
                      setShowPaymentModal(false);
                      setPaymentMethod(null);
                      setSplitMode(false);
                      setPaymentMethodsUsed([]);
                      setRemainingAmount(0);
                      setManualAmount('');
                    }}
                    className="w-full py-2 rounded bg-red-500 hover:bg-red-600 text-white"
                  >
                    Cancel Payment
                  </button>

                  {/* Confirm Payment (Split Mode) */}
                  {splitMode && remainingAmount <= 0 && (
                    <button
                      onClick={() => finalizeTransaction(paymentMethodsUsed)}
                      disabled={!isOnline}
                      className={`w-full py-2 rounded ${!isOnline
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-green-500 hover:bg-green-600 text-white"
                        }`}
                    >
                      {isOnline ? "Confirm Payment" : "Offline - Cannot Send Order"}
                    </button>
                  )}
                </div>
              </div>
            )}

          </div>
        </>
      )}
      {showChangeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-lg max-w-md w-full space-y-6 text-center">
            <h2 className="text-2xl font-bold text-[#4c5d99]">Order Complete</h2>
            <p className="text-lg text-gray-700">
              Change to give: <span className="font-bold text-green-600">${changeDue.toFixed(2)}</span>
            </p>
            <button
              className="mt-4 px-6 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-lg"
              onClick={() => {
                setShowChangeModal(false);
                resetOrder();
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
      {orderCompletedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div ref={orderCompletedRef} className="bg-white p-8 rounded-lg max-w-md w-full text-center space-y-4">
            {orderBeingSent ? (
              <div className="flex flex-col items-center justify-center space-y-4">
                <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-sm text-gray-600">Processing your order...</p>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-green-600">Order #{orderIdCreated} Completed</h2>
                {changeDue > 0 && (
                  <p className="text-lg text-gray-700">
                    Change to give: <span className="font-bold text-green-600">${changeDue.toFixed(2)}</span>
                  </p>
                )}

                <div className="text-left mt-6 border-t pt-4 text-sm space-y-1">
                  {groupedItems.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">{item.quantity}x {item.name}</p>
                        {item.extras?.length > 0 && (
                          <ul className="text-xs text-gray-500 pl-4">
                            {item.extras.map((e, i) => (
                              <li key={i}>+ {e.quantity}x {e.name} (${(e.quantity * e.price).toFixed(2)})</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <p className="font-bold">${item.total.toFixed(2)}</p>
                    </div>
                  ))}
                  <hr className="my-2" />
                  <p>Subtotal: ${subtotal.toFixed(2)}</p>
                  <p>Discount: -${discountValue.toFixed(2)} ({discount.percent}%)</p>
                  <p>GST: ${gst.toFixed(2)}</p>
                  <p>QST: ${qst.toFixed(2)}</p>
                  <p className="font-bold">Total: ${grandTotal.toFixed(2)}</p>
                </div>

                <div className="flex justify-center gap-4 mt-6">
                  <button
                    onClick={() => {
                      setOrderCompletedModal(false);
                      setOrderIdCreated(null);
                      setChangeDue(0);
                      setEmailSentTo("");
                      resetOrder();
                    }}
                    className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-black rounded-lg"
                  >
                    No Receipt
                  </button>
                  <button
                    onClick={() => {
                      setOrderCompletedModal(false); // ✅ hides background
                      setShowEmailModal(true);       // ✅ opens receipt modal
                      setCustomerEmail("");          // ✅ reset email input
                    }}
                    className="px-6 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg"
                  >
                    Email Receipt
                  </button>
                </div>

                <button
                  disabled={orderBeingSent}
                  className={`mt-4 px-6 py-3 rounded-lg text-lg ${orderBeingSent
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-indigo-500 hover:bg-indigo-600 text-white"
                    }`}
                  onClick={() => {
                    setOrderCompletedModal(false);
                    setOrderIdCreated(null);
                    setChangeDue(0);
                    resetOrder();
                  }}
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div
            className="bg-white p-6 rounded-lg max-w-sm w-full text-center space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-[#4c5d99]">Send Receipt by Email</h3>
            <input
              type="email"
              placeholder="Enter customer's email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded"
            />

            <div className="flex justify-center gap-4 mt-4">
              <button
                type="button"
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEmailModal(false);
                  setCustomerEmail(""); // ✅ Clear previous input
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                className={`px-4 py-2 rounded ${emailSending ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                disabled={emailSending}
                onClick={async (e) => {
                  e.stopPropagation();

                  if (!customerEmail.includes("@")) {
                    alert("Please enter a valid email.");
                    return;
                  }

                  console.log("📤 Email send button clicked with:", customerEmail);

                  setEmailSending(true);

                  const payload = {
                    email: customerEmail,
                    ...(receiptSnapshot || {
                      orderID: orderIdCreated,
                      items: groupedItems.map(item => ({
                        name: item.name,
                        quantity: item.quantity,
                        total: item.total.toFixed(2)
                      })),
                      subtotal: subtotal.toFixed(2),
                      discountValue: discountValue.toFixed(2),
                      gst: gst.toFixed(2),
                      qst: qst.toFixed(2),
                      grandTotal: (paymentMethod === "Cash" ? roundedTotal : grandTotal).toFixed(2),
                      timestamp: localTimestamp
                    })
                  };

                  try {
                    console.log("📧 Sending email receipt to:", customerEmail);
                    console.log("📦 Email Payload:", payload);

                    const res = await fetch("https://tifsu-ordering.onrender.com/api/send-receipt", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify(payload),
                    });

                    if (!res.ok) {
                      console.error("❌ Email sending failed - response not ok");
                      throw new Error("Failed to send email");
                    }

                    console.log("✅ Email successfully sent to:", customerEmail);

                    setShowEmailModal(false);        // ✅ hide modal
                    setOrderCompletedModal(false);   // ✅ hide background
                    setEmailSentTo(customerEmail);   // ✅ show new confirmation
                    setShowEmailConfirmation(true);  // ✅ trigger checkmark modal

                    // Optional: reset email after showing
                    setTimeout(() => setCustomerEmail(""), 2000);

                  } catch (err) {
                    console.error("❌ Error during email send:", err);
                    alert("Failed to send email.");
                  } finally {
                    setEmailSending(false);
                  }
                }}
              >
                {emailSending ? 'Sending...' : 'Send Receipt'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEmailConfirmation && (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-sm w-full space-y-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-green-600 mx-auto" fill="none"
              viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <h3 className="text-xl font-bold text-green-600">Receipt Sent!</h3>
            <p className="text-sm text-gray-700">Sent to: <strong>{emailSentTo}</strong></p>
            <button
              className="mt-2 px-6 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded"
              onClick={() => {
                setShowEmailConfirmation(false);
                setEmailSentTo("");
                setOrderCompletedModal(false);
                resetOrder(); // 🧼 fully reset
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {emailSending && (
        <div className="fixed inset-0 flex justify-center items-center bg-white bg-opacity-80 z-50">
          <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {showEmailConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-sm w-full text-center space-y-4">
            <div className="flex justify-center text-green-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none"
                viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-green-600">Receipt Sent!</h3>
            <p className="text-gray-700 text-sm">Sent to: <strong>{emailSentTo}</strong></p>
            <button
              onClick={() => {
                setShowEmailConfirmation(false);
                setEmailSentTo("");
                setOrderCompletedModal(false);
                setOrderIdCreated(null);
                setChangeDue(0);
                resetOrder();
              }}
              className="mt-4 px-6 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg"
            >
              Done
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default OrderPage